import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Table,
  Badge,
  Alert,
  Spinner,
  Form,
  InputGroup,
  Pagination,
  Modal,
  ListGroup,
  Tabs,
  Tab,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { fires } from "../../firebase";
import firebase from "firebase/app";
import "firebase/firestore";
import { CSVLink } from "react-csv";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import moment from 'moment';
import { FaDownload, FaEye, FaSearch, FaFilter, FaSync, FaEdit, FaTrash, FaPlus, FaCalendarAlt } from "react-icons/fa";

export default function AllBusinessesManagement() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Business data (events with type='business')
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  
  // Business orders and coupons (filtered by business eventIds)
  const [businessOrders, setBusinessOrders] = useState([]);
  const [businessCoupons, setBusinessCoupons] = useState([]);
  const [businessViews, setBusinessViews] = useState([]);
  const [allBusinessViews, setAllBusinessViews] = useState([]); // Store all views for filtering
  const [userDataMap, setUserDataMap] = useState({}); // Map of UID to user data
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [loadingViews, setLoadingViews] = useState(false);
  const [businessesLoaded, setBusinessesLoaded] = useState(false);
  const [usedCouponCodes, setUsedCouponCodes] = useState(new Set());
  
  // Date range for views filtering
  const [viewsStartDate, setViewsStartDate] = useState(moment().startOf('day').subtract(30, 'days').toDate());
  const [viewsEndDate, setViewsEndDate] = useState(moment().endOf('day').toDate());
  
  // Search and filter
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [activeTab, setActiveTab] = useState("businesses");
  
  // Business details modal
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  
  // Edit business modal
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Analytics
  const [businessAnalytics, setBusinessAnalytics] = useState({
    totalBusinesses: 0,
    activeBusinesses: 0,
    inactiveBusinesses: 0,
    totalOrders: 0,
    totalRevenue: 0,
    totalCoupons: 0,
    totalViews: 0,
  });

  // Coupon creation
  const [couponAmount, setCouponAmount] = useState(5);
  const [couponQuantity, setCouponQuantity] = useState(1);
  const [creatingCoupons, setCreatingCoupons] = useState(false);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        if (!currentUser) {
          navigate('/');
          return;
        }
        
        const adminDoc = await fires.collection('admin').doc(currentUser.uid).get();
        if (!adminDoc.exists) {
          setIsAdmin(false);
          setLoading(false);
          navigate('/');
          return;
        }
        
        // Allow any admin to access - no role restriction here
        setIsAdmin(true);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setError('Error verifying admin access');
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [currentUser, navigate]);

  // Load all business-related data
  useEffect(() => {
    if (isAdmin) {
      loadBusinesses();
    }
  }, [isAdmin]);

  // Load orders and coupons when businesses are loaded (only once per business list change)
  useEffect(() => {
    if (businesses.length > 0 && !businessesLoaded) {
      setBusinessesLoaded(true);
      loadBusinessOrders();
    } else if (businesses.length === 0) {
      setBusinessesLoaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businesses.length, businessesLoaded]); // Only depend on length, not the entire array

  // Load coupons after orders are loaded (to check which coupons are used)
  useEffect(() => {
    if (businesses.length > 0 && businessOrders.length >= 0) {
      // Only load if we have businesses and orders have been processed
      loadBusinessCoupons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usedCouponCodes, businesses.length]);

  // Load views when businesses are loaded
  useEffect(() => {
    if (businesses.length > 0) {
      loadBusinessViews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businesses.length]);

  // Note: Date filtering is now manual via "Apply Filter" button

  // Filter businesses when search term or filter changes
  useEffect(() => {
    filterBusinesses();
  }, [businesses, searchTerm, filterStatus]);

  const loadBusinesses = async () => {
    try {
      setLoading(true);
      setError("");
      
      // Load events with type='business' as businesses
      let eventsSnapshot;
      try {
        eventsSnapshot = await fires
          .collection('events')
          .where('type', '==', 'business')
          .orderBy('timestamp', 'desc')
          .get();
      } catch (indexError) {
        // If index doesn't exist, get all and filter
        const allEvents = await fires.collection('events').get();
        const filtered = allEvents.docs
          .filter(doc => doc.data().type === 'business')
          .sort((a, b) => {
            const aTime = a.data().timestamp?.toMillis?.() || 0;
            const bTime = b.data().timestamp?.toMillis?.() || 0;
            return bTime - aTime; // Descending
          });
        eventsSnapshot = { docs: filtered };
      }
      
      const businessesData = [];
      for (const businessDoc of eventsSnapshot.docs) {
        const businessData = businessDoc.data();
        const isApproved = businessData.approved === true;
        
        businessesData.push({
          id: businessDoc.id,
          ...businessData,
          approved: isApproved,
          createdAt: businessData.timestamp?.toDate?.() || new Date(),
          status: businessData.status || 'live',
          name: businessData.title || businessData.name || 'Unnamed Business',
          email: businessData.email || businessData.user?.email || '',
          phone: businessData.phone || '',
          location: businessData.location || businessData.address?.fullAddress || '',
          category: businessData.category || businessData.type || 'business',
        });
      }
      
      setBusinesses(businessesData);
      setBusinessesLoaded(false); // Reset flag when businesses are reloaded
      calculateBusinessAnalytics(businessesData);
    } catch (err) {
      console.error('Error loading businesses:', err);
      setError('Failed to load businesses: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadBusinessOrders = async () => {
    try {
      setLoadingOrders(true);
      // Get all business event IDs
      const businessEventIds = businesses.map(b => b.id);
      
      if (businessEventIds.length === 0) {
        setBusinessOrders([]);
        setLoadingOrders(false);
        return;
      }

      // Load orders where eventId is in the list of business event IDs
      const allOrders = await fires.collection('orders').get();
      const ordersData = [];
      let totalRevenue = 0;
      
      // Track used coupon codes
      const usedCodes = new Set();
      
      // Calculate sales per business
      const businessSalesMap = {};
      businesses.forEach(business => {
        businessSalesMap[business.id] = 0;
      });
      
      for (const orderDoc of allOrders.docs) {
        const orderData = orderDoc.data();
        // Check if order is for a business event
        if (orderData.eventId && businessEventIds.includes(orderData.eventId)) {
          const orderAmount = orderData.amount || 0;
          totalRevenue += orderAmount;
          
          // Track coupon code if used in this order
          if (orderData.couponCode) {
            usedCodes.add(orderData.couponCode);
          }
          
          // Add to business sales
          if (businessSalesMap[orderData.eventId] !== undefined) {
            businessSalesMap[orderData.eventId] += orderAmount;
          }
          
          ordersData.push({
            id: orderDoc.id,
            ...orderData,
            createdAt: orderData.timestamp?.toDate?.() || new Date(),
          });
        }
      }
      
      // Update used coupon codes state
      setUsedCouponCodes(usedCodes);
      
      // Sort by timestamp descending
      ordersData.sort((a, b) => {
        const aTime = a.createdAt?.getTime?.() || 0;
        const bTime = b.createdAt?.getTime?.() || 0;
        return bTime - aTime;
      });
      
      // Update businesses with sales data (only update sales, don't create new objects unnecessarily)
      setBusinesses(prev => {
        const updated = prev.map(business => ({
          ...business,
          sales: businessSalesMap[business.id] || 0,
        }));
        // Only update if sales actually changed to prevent unnecessary re-renders
        return updated;
      });
      
      setBusinessOrders(ordersData);
      setBusinessAnalytics(prev => ({ 
        ...prev, 
        totalOrders: ordersData.length,
        totalRevenue: totalRevenue 
      }));
    } catch (err) {
      console.error('Error loading business orders:', err);
      setError('Failed to load business orders: ' + err.message);
    } finally {
      setLoadingOrders(false);
    }
  };

  const loadBusinessCoupons = async () => {
    try {
      setLoadingCoupons(true);
      // Get all business event IDs
      const businessEventIds = businesses.map(b => b.id);
      
      if (businessEventIds.length === 0) {
        setBusinessCoupons([]);
        setLoadingCoupons(false);
        return;
      }

      // Try to load coupons with indexed query first, fallback to loading all if index doesn't exist
      let couponsSnapshot;
      try {
        // Try with orderBy first (requires composite index)
        couponsSnapshot = await fires
          .collection('promos')
          .where('type', '==', 'business')
          .orderBy('created', 'desc')
          .get();
      } catch (indexError) {
        console.log('Index error, trying without orderBy:', indexError);
        try {
          // Try without orderBy (just type filter)
          couponsSnapshot = await fires
            .collection('promos')
            .where('type', '==', 'business')
            .get();
        } catch (filterError) {
          console.log('Filter error, loading all and filtering:', filterError);
          // If that fails too, load all coupons and filter manually
          const allCoupons = await fires.collection('promos').get();
          const filtered = allCoupons.docs.filter(doc => {
            const data = doc.data();
            return data.type === 'business';
          });
          couponsSnapshot = { docs: filtered };
        }
      }
      
      const couponsData = [];
      for (const couponDoc of couponsSnapshot.docs) {
        const couponData = couponDoc.data();
        const couponCode = couponData.code || couponDoc.id;
        
        // Check if coupon code has been used in orders
        const isUsed = usedCouponCodes.has(couponCode);
        
        couponsData.push({
          id: couponDoc.id,
          ...couponData,
          createdAt: couponData.created?.toDate?.() || couponData.timestamp?.toDate?.() || new Date(),
          isUsed: isUsed,
          isValid: isUsed ? false : (couponData.validated !== false), // Invalid if used, otherwise use original validated status
        });
      }
      
      // Sort by created date descending if not already sorted
      couponsData.sort((a, b) => {
        const aTime = a.createdAt?.getTime?.() || 0;
        const bTime = b.createdAt?.getTime?.() || 0;
        return bTime - aTime; // Descending
      });
      
      setBusinessCoupons(couponsData);
      setBusinessAnalytics(prev => ({ ...prev, totalCoupons: couponsData.length }));
    } catch (err) {
      console.error('Error loading business coupons:', err);
      setError('Failed to load business coupons: ' + err.message);
      setBusinessCoupons([]);
    } finally {
      setLoadingCoupons(false);
    }
  };

  const loadBusinessViews = async () => {
    try {
      setLoadingViews(true);
      
      // Get all business event IDs for matching
      const businessEventIds = businesses.map(b => b.id);
      const businessEventIdsSet = new Set(businessEventIds);
      
      // Load all page views and filter for lifestyle URLs
      // Since Firestore doesn't support regex queries, we need to load all and filter
      let allViews;
      try {
        // Try to get all views ordered by timestamp (requires index)
        allViews = await fires
          .collection('page_views')
          .orderBy('timestamp', 'desc')
          .limit(10000) // Limit to prevent memory issues
          .get();
      } catch (indexError) {
        console.log('Index error, loading all views without orderBy:', indexError);
        // Fallback: load all views without orderBy
        allViews = await fires.collection('page_views').get();
      }
      
      const viewsData = [];
      const businessViewCountMap = {}; // Track view counts per business
      
      // Initialize view counts
      businesses.forEach(business => {
        businessViewCountMap[business.id] = 0;
      });
      
      for (const viewDoc of allViews.docs) {
        const viewData = viewDoc.data();
        const url = viewData.url || '';
        
        // Filter for URLs containing /lifestyles or /lifestyle/
        if (url.includes('/lifestyles') || url.includes('/lifestyle/')) {
          // Extract eventId from URL if possible (format: /lifestyle/{eventId})
          let eventId = viewData.eventId || '';
          
          // Try to extract eventId from URL
          const lifestyleMatch = url.match(/\/lifestyle[s]?\/([^\/\?#]+)/);
          if (lifestyleMatch && lifestyleMatch[1]) {
            const extractedEventId = lifestyleMatch[1];
            if (businessEventIdsSet.has(extractedEventId)) {
              eventId = extractedEventId;
              businessViewCountMap[extractedEventId] = (businessViewCountMap[extractedEventId] || 0) + 1;
            }
          }
          
          // Also count if eventId is directly in the view data
          if (viewData.eventId && businessEventIdsSet.has(viewData.eventId)) {
            businessViewCountMap[viewData.eventId] = (businessViewCountMap[viewData.eventId] || 0) + 1;
            eventId = viewData.eventId;
          }
          
          viewsData.push({
            id: viewDoc.id,
            url: url,
            eventId: eventId,
            timestamp: viewData.timestamp?.toDate?.() || new Date(),
            currentIP: viewData.currentIP || 'N/A',
            platform: viewData.platform || 'N/A',
            device: viewData.device || 'N/A',
            fromUA: viewData.fromUA || 'N/A',
            uid: viewData.uid || null, // User ID if logged in
            city: viewData.city || '',
            region: viewData.region || '',
            location: viewData.city && viewData.region 
              ? `${viewData.city}, ${viewData.region}` 
              : (viewData.city || viewData.region || 'N/A'),
          });
        }
      }
      
      // Sort by timestamp descending
      viewsData.sort((a, b) => {
        const aTime = a.timestamp?.getTime?.() || 0;
        const bTime = b.timestamp?.getTime?.() || 0;
        return bTime - aTime;
      });
      
      // Store all views for filtering
      setAllBusinessViews(viewsData);
      
      // Load user data for all unique UIDs
      await loadUserDataForViews(viewsData);
      
      // Filter by initial date range
      filterViewsByDateRange(viewsData);
      
      // Update businesses with view counts (based on all views, not filtered)
      setBusinesses(prev => {
        return prev.map(business => ({
          ...business,
          views: businessViewCountMap[business.id] || 0,
        }));
      });
      
      setBusinessAnalytics(prev => ({ 
        ...prev, 
        totalViews: viewsData.length 
      }));
    } catch (err) {
      console.error('Error loading business views:', err);
      setError('Failed to load business views: ' + err.message);
      setBusinessViews([]);
      setAllBusinessViews([]);
    } finally {
      setLoadingViews(false);
    }
  };

  const loadUserDataForViews = async (views) => {
    try {
      // Collect all unique UIDs from views
      const uniqueUIDs = new Set();
      views.forEach(view => {
        if (view.uid) {
          uniqueUIDs.add(view.uid);
        }
      });

      if (uniqueUIDs.size === 0) {
        return; // No user IDs to load
      }

      // Batch fetch user data
      const userIdsArray = Array.from(uniqueUIDs);
      const userDataPromises = userIdsArray.map(async (uid) => {
        try {
          const userDoc = await fires.collection('users').doc(uid).get();
          if (userDoc.exists) {
            const userData = userDoc.data();
            return {
              uid: uid,
              email: userData.email || '',
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              displayName: userData.displayName || '',
              phone: userData.phone || '',
            };
          }
        } catch (error) {
          console.error(`Error loading user data for UID ${uid}:`, error);
        }
        return null;
      });

      const userDataResults = await Promise.all(userDataPromises);
      
      // Build user data map
      const newUserDataMap = {};
      userDataResults.forEach(userData => {
        if (userData) {
          newUserDataMap[userData.uid] = userData;
        }
      });

      setUserDataMap(prev => ({
        ...prev,
        ...newUserDataMap
      }));
    } catch (error) {
      console.error('Error loading user data for views:', error);
    }
  };

  const filterViewsByDateRange = (viewsToFilter = null) => {
    const views = viewsToFilter || allBusinessViews;
    if (views.length === 0) {
      setBusinessViews([]);
      return;
    }

    const start = moment(viewsStartDate).startOf('day').toDate();
    const end = moment(viewsEndDate).endOf('day').toDate();

    const filtered = views.filter(view => {
      const viewDate = view.timestamp instanceof Date ? view.timestamp : new Date(view.timestamp);
      return viewDate >= start && viewDate <= end;
    });

    // Recalculate view counts for filtered data
    const businessViewCountMap = {};
    businesses.forEach(business => {
      businessViewCountMap[business.id] = 0;
    });

    filtered.forEach(view => {
      if (view.eventId && businessViewCountMap[view.eventId] !== undefined) {
        businessViewCountMap[view.eventId] = (businessViewCountMap[view.eventId] || 0) + 1;
      }
    });

    setBusinessViews(filtered);
    
    // Update analytics with filtered count
    setBusinessAnalytics(prev => ({ 
      ...prev, 
      totalViews: filtered.length 
    }));
  };

  // Calculate daily view counts
  const getDailyViewCounts = () => {
    if (businessViews.length === 0) return [];

    const dailyCounts = {};
    
    businessViews.forEach(view => {
      const viewDate = view.timestamp instanceof Date ? view.timestamp : new Date(view.timestamp);
      const dayKey = moment(viewDate).format('YYYY-MM-DD');
      
      if (!dailyCounts[dayKey]) {
        dailyCounts[dayKey] = {
          date: dayKey,
          displayDate: moment(viewDate).format('MMM DD, YYYY'),
          count: 0
        };
      }
      dailyCounts[dayKey].count++;
    });

    // Convert to array and sort by date descending
    const dailyArray = Object.values(dailyCounts).sort((a, b) => {
      return moment(b.date).diff(moment(a.date));
    });

    return dailyArray;
  };

  // Calculate most active visitors
  const getMostActiveVisitors = () => {
    if (businessViews.length === 0) return [];

    const visitorCounts = {};
    
    businessViews.forEach(view => {
      if (view.uid) {
        if (!visitorCounts[view.uid]) {
          visitorCounts[view.uid] = {
            uid: view.uid,
            count: 0,
            userInfo: userDataMap[view.uid] || null
          };
        }
        visitorCounts[view.uid].count++;
      }
    });

    // Convert to array and sort by count descending
    const visitorArray = Object.values(visitorCounts).sort((a, b) => b.count - a.count);

    return visitorArray;
  };

  // Calculate most viewed businesses
  const getMostViewedBusinesses = () => {
    if (businessViews.length === 0) return [];

    const businessViewCounts = {};
    
    businessViews.forEach(view => {
      if (view.eventId) {
        if (!businessViewCounts[view.eventId]) {
          const business = businesses.find(b => b.id === view.eventId);
          businessViewCounts[view.eventId] = {
            eventId: view.eventId,
            businessName: business?.name || business?.title || 'Unknown Business',
            count: 0
          };
        }
        businessViewCounts[view.eventId].count++;
      }
    });

    // Convert to array and sort by count descending
    const businessArray = Object.values(businessViewCounts).sort((a, b) => b.count - a.count);

    return businessArray;
  };

  const handleApplyViewsDateRange = () => {
    filterViewsByDateRange();
  };

  const calculateBusinessAnalytics = (businessesData) => {
    const analytics = {
      totalBusinesses: businessesData.length,
      activeBusinesses: businessesData.filter(b => b.approved === true).length,
      inactiveBusinesses: businessesData.filter(b => !b.approved).length,
      totalOrders: businessOrders.length,
      totalRevenue: businessOrders.reduce((sum, order) => sum + (order.amount || 0), 0),
      totalCoupons: businessCoupons.length,
      totalViews: businessViews.length,
    };
    setBusinessAnalytics(analytics);
  };

  const filterBusinesses = () => {
    let filtered = [...businesses];

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(business => 
        business.name?.toLowerCase().includes(search) ||
        business.title?.toLowerCase().includes(search) ||
        business.email?.toLowerCase().includes(search) ||
        business.phone?.toLowerCase().includes(search) ||
        business.location?.toLowerCase().includes(search) ||
        business.id?.toLowerCase().includes(search)
      );
    }

    // Apply approved status filter
    if (filterStatus !== 'all') {
      if (filterStatus === 'active') {
        filtered = filtered.filter(b => b.approved === true);
      } else if (filterStatus === 'inactive') {
        filtered = filtered.filter(b => !b.approved);
      } else {
        filtered = filtered.filter(b => b.status === filterStatus);
      }
    }

    setFilteredBusinesses(filtered);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleViewBusiness = (business) => {
    setSelectedBusiness(business);
    setShowBusinessModal(true);
  };

  const handleEditBusiness = (business) => {
    setEditingBusiness({ ...business });
    setShowEditModal(true);
  };

  function generateInvitationCode() {
    var sValidCharacters = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    var sCharCode = "";
    for (let i = 0; i < 10; i++) {
        sCharCode = sCharCode + sValidCharacters.charAt(parseInt(Math.random() * sValidCharacters.length));
    }
    
    return sCharCode
}   
  async function createLifestyle5DollarCouponCode(){
    const code = generateInvitationCode()
    await fires
      .collection("promos")
      .doc(code)
      .set({
        amount: 5,
        code: code,
        created: firebase.firestore.Timestamp.fromDate(new Date()),
        eventId: "1000339521483",
        orgId: "",
        promoType: 'AMOUNT',
        promotionCode: 'promo_1SNz3lDQLADuO6no5fPWGE2V',
        type: 'business',
        redeem:"once",
        validated: true,
      })
    return code;
  }
  async function createLifestyle30DollarCouponCode(){
    const code = generateInvitationCode()
    await fires
      .collection("promos")
      .doc(code)
      .set({
        amount: 30,
        code: code,
        created: firebase.firestore.Timestamp.fromDate(new Date()),
        timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
        eventId: "1000339521483",
        orgId: "",
        promoType: 'AMOUNT',
        promotionCode: 'promo_1SNFYEDQLADuO6noqfROgpPM',
        type: 'business',
        redeem:"once",
        validated: true,
      })
    return code;
  }

  const handleCreateCoupons = async () => {
    if (!couponQuantity || couponQuantity < 1 || couponQuantity > 100) {
      alert('Please enter a valid quantity between 1 and 100');
      return;
    }

    try {
      setCreatingCoupons(true);
      const codes = [];
      
      for (let i = 0; i < couponQuantity; i++) {
        let code;
        if (couponAmount === 5) {
          code = await createLifestyle5DollarCouponCode();
        } else {
          code = await createLifestyle30DollarCouponCode();
        }
        codes.push(code);
      }

      alert(`Successfully created ${codes.length} coupon${codes.length > 1 ? 's' : ''}!\nCodes: ${codes.join(', ')}`);
      
      // Reset form
      setCouponQuantity(1);
      
      // Reload coupons
      await loadBusinessCoupons();
    } catch (err) {
      console.error('Error creating coupons:', err);
      alert('Failed to create coupons: ' + err.message);
    } finally {
      setCreatingCoupons(false);
    }
  };

  const handleSaveBusiness = async () => {
    try {
      setSaving(true);
      const { id, createdAt, ...updateData } = editingBusiness;
      
      await fires.collection('events').doc(id).update({
        ...updateData,
        updatedAt: new Date(),
      });
      
      setShowEditModal(false);
      setEditingBusiness(null);
      await loadBusinesses();
      alert('Business updated successfully!');
    } catch (err) {
      console.error('Error saving business:', err);
      alert('Failed to save business: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    if (!window.confirm('Are you sure you want to delete this business? This action cannot be undone.')) {
      return;
    }

    try {
      await fires.collection('events').doc(businessId).delete();
      await loadBusinesses();
      alert('Business deleted successfully!');
    } catch (err) {
      console.error('Error deleting business:', err);
      alert('Failed to delete business: ' + err.message);
    }
  };

  const getApprovedBadge = (approved) => {
    if (approved === true) {
      return <Badge bg="success">Approved</Badge>;
    } else {
      return <Badge bg="secondary">Not Approved</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { variant: 'success', text: 'Completed' },
      pending: { variant: 'warning', text: 'Pending' },
      cancelled: { variant: 'danger', text: 'Cancelled' },
      refunded: { variant: 'info', text: 'Refunded' },
      active: { variant: 'success', text: 'Active' },
      inactive: { variant: 'secondary', text: 'Inactive' },
    };

    const config = statusConfig[status] || { variant: 'info', text: status || 'N/A' };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date instanceof Date ? date : date.toDate();
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(d);
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBusinesses = filteredBusinesses.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBusinesses.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // CSV Export for Businesses
  const generateCSVData = () => {
    return filteredBusinesses.map(business => ({
      'ID': business.id,
      'Name': business.name || 'N/A',
      'Email': business.email || 'N/A',
      'Phone': business.phone || 'N/A',
      'Location': business.location || 'N/A',
      'Sales': `$${(business.sales || 0).toFixed(2)}`,
      'Views': business.views || 0,
      'Approved': business.approved ? 'Yes' : 'No',
      'Created At': formatDate(business.createdAt),
    }));
  };

  // CSV Export for Coupons
  const generateCouponsCSVData = () => {
    return businessCoupons.map(coupon => {
      let couponValue = 'N/A';
      if (coupon.amount) {
        if (coupon.promoType === 'PERCENTAGE') {
          couponValue = `${coupon.amount}%`;
        } else if (coupon.promoType === 'AMOUNT') {
          couponValue = `$${coupon.amount}`;
        } else if (coupon.promoType === 'FREE') {
          couponValue = '$0 (Free)';
        } else {
          couponValue = `$${coupon.amount}`;
        }
      }
      
      return {
        'Coupon Code': coupon.code || coupon.id,
        'Value': couponValue,
        'Type': coupon.promoType || 'N/A',
        'Status': coupon.isUsed ? 'Used (Invalid)' : (coupon.isValid ? 'Valid' : 'Invalid'),
        'Redeem Limit': coupon.redeem || 'Unlimited',
        'Event ID': coupon.eventId || 'N/A',
        'Created At': formatDate(coupon.createdAt),
      };
    });
  };

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading businesses...</p>
        </div>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>You do not have permission to access this page.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>Business Management</h2>
              <p className="text-muted">Manage all businesses in the system</p>
            </div>
            <Button
              variant="primary"
              onClick={() => navigate('/business-price-management')}
              className="ms-3"
            >
              <FaEdit className="me-2" />
              Price Management
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Analytics Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card>
            <Card.Body>
              <Card.Title>Total Businesses</Card.Title>
              <h2>{businessAnalytics.totalBusinesses}</h2>
              <small className="text-muted">Events with type='business'</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <Card.Title>Active Businesses</Card.Title>
              <h2 className="text-success">{businessAnalytics.activeBusinesses}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <Card.Title>Total Orders</Card.Title>
              <h2 className="text-primary">{businessAnalytics.totalOrders}</h2>
              <small className="text-muted">${businessAnalytics.totalRevenue.toFixed(2)} revenue</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <Card.Title>Coupons</Card.Title>
              <h2 className="text-warning">{businessAnalytics.totalCoupons}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <Card.Title>Event Views</Card.Title>
              <h2 className="text-info">{businessAnalytics.totalViews || 0}</h2>
              <small className="text-muted">URLs with /lifestyle</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Search and Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search businesses by title, email, phone, location, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All</option>
                <option value="active">Approved</option>
                <option value="inactive">Not Approved</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <div className="d-flex gap-2">
                <Button variant="outline-primary" onClick={() => {
                  loadBusinesses();
                  // Orders, coupons, and views will reload automatically when businesses reload
                }}>
                  <FaSync /> Refresh All
                </Button>
                <CSVLink
                  data={generateCSVData()}
                  filename={`businesses-${new Date().toISOString().split('T')[0]}.csv`}
                  className="btn btn-outline-success"
                >
                  <FaDownload /> Export CSV
                </CSVLink>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Main Content Tabs */}
      <Card>
        <Card.Header>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-0"
          >
            <Tab eventKey="businesses" title={`Businesses (${filteredBusinesses.length})`} />
            <Tab eventKey="orders" title={`Orders (${businessOrders.length})`} />
            <Tab eventKey="coupons" title={`Coupons (${businessCoupons.length})`} />
            <Tab eventKey="views" title={`Views (${businessViews.length})`} />
          </Tabs>
        </Card.Header>
        <Card.Body>
          {/* Businesses Tab */}
          {activeTab === "businesses" && (
            <div>
          {filteredBusinesses.length === 0 ? (
            <Alert variant="info">No businesses found.</Alert>
          ) : (
            <>
              <Table responsive striped bordered hover>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Location</th>
                    <th>Sales</th>
                    <th>Views</th>
                    <th>Approved</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentBusinesses.map((business) => (
                    <tr key={business.id}>
                      <td>
                        <small className="text-muted">{business.id.substring(0, 8)}...</small>
                      </td>
                      <td>{business.name || 'N/A'}</td>
                      <td>{business.email || 'N/A'}</td>
                      <td>{business.phone || 'N/A'}</td>
                      <td>{business.location || 'N/A'}</td>
                      <td>
                        <strong className="text-success">
                          ${(business.sales || 0).toFixed(2)}
                        </strong>
                      </td>
                      <td>
                        <strong className="text-info">
                          {business.views || 0}
                        </strong>
                      </td>
                      <td>{getApprovedBadge(business.approved)}</td>
                      <td>{formatDate(business.createdAt)}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="info"
                            onClick={() => handleViewBusiness(business)}
                          >
                            <FaEye />
                          </Button>
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() => navigate(`/admin-business-management/${business.id}`)}
                          >
                            Modify
                          </Button>
                          <Button
                            size="sm"
                            variant="warning"
                            onClick={() => navigate(`/business-price-management/${business.id}`)}
                          >
                            Prices
                          </Button>
                          <Button
                            size="sm"
                            variant="info"
                            onClick={() => window.open(`https://www.eventgo.ca/lifestyle/${business.id}`, '_blank')}
                          >
                            View Event
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.First
                      onClick={() => paginate(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    {[...Array(totalPages)].map((_, index) => {
                      const page = index + 1;
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 2 && page <= currentPage + 2)
                      ) {
                        return (
                          <Pagination.Item
                            key={page}
                            active={page === currentPage}
                            onClick={() => paginate(page)}
                          >
                            {page}
                          </Pagination.Item>
                        );
                      } else if (
                        page === currentPage - 3 ||
                        page === currentPage + 3
                      ) {
                        return <Pagination.Ellipsis key={page} />;
                      }
                      return null;
                    })}
                    <Pagination.Next
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => paginate(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === "orders" && (
            <div>
              {loadingOrders ? (
                <div className="text-center">
                  <Spinner animation="border" />
                  <p className="mt-2">Loading orders...</p>
                </div>
              ) : businessOrders.length === 0 ? (
                <Alert variant="info">No business orders found.</Alert>
              ) : (
                <Table responsive striped bordered hover>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>User Email</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Coupon Code</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businessOrders.map((order) => (
                      <tr key={order.id}>
                        <td><small className="text-muted">{order.uniqueId || order.id.substring(0, 12)}...</small></td>
                        <td>{order.userEmail || 'N/A'}</td>
                        <td>${(order.amount || 0).toFixed(2)}</td>
                        <td>{getStatusBadge(order.status || 'completed')}</td>
                        <td>{order.couponCode || '-'}</td>
                        <td>{formatDate(order.createdAt)}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="info"
                            onClick={() => window.open(`/admin-order-details/${order.uniqueId || order.id}`, '_blank')}
                          >
                            <FaEye /> View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          )}

          {/* Coupons Tab */}
          {activeTab === "coupons" && (
            <div>
              {/* Create Coupons Section */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Create New Coupons</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Coupon Value</Form.Label>
                        <div>
                          <Form.Check
                            inline
                            type="radio"
                            name="couponAmount"
                            id="coupon5"
                            label="$5"
                            value={5}
                            checked={couponAmount === 5}
                            onChange={(e) => setCouponAmount(5)}
                          />
                          <Form.Check
                            inline
                            type="radio"
                            name="couponAmount"
                            id="coupon30"
                            label="$30"
                            value={30}
                            checked={couponAmount === 30}
                            onChange={(e) => setCouponAmount(30)}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Quantity</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max="100"
                          value={couponQuantity}
                          onChange={(e) => setCouponQuantity(parseInt(e.target.value) || 1)}
                          placeholder="Enter quantity"
                        />
                        <Form.Text className="text-muted">
                          Enter a number between 1 and 100
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4} className="d-flex align-items-end">
                      <Button
                        variant="primary"
                        onClick={handleCreateCoupons}
                        disabled={creatingCoupons}
                        className="w-100"
                      >
                        {creatingCoupons ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <FaPlus className="me-2" />
                            Create Coupons
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {loadingCoupons ? (
                <div className="text-center">
                  <Spinner animation="border" />
                  <p className="mt-2">Loading coupons...</p>
                </div>
              ) : businessCoupons.length === 0 ? (
                <Alert variant="info">No business coupons found.</Alert>
              ) : (
                <>
                  <div className="mb-3 d-flex justify-content-end">
                    <CSVLink
                      data={generateCouponsCSVData()}
                      filename={`business-coupons-${new Date().toISOString().split('T')[0]}.csv`}
                      className="btn btn-outline-success"
                    >
                      <FaDownload /> Export Coupons CSV
                    </CSVLink>
                  </div>
                  <Table responsive striped bordered hover>
                  <thead>
                    <tr>
                      <th>Coupon Code</th>
                      <th>Value</th>
                      <th>Type</th>
                      <th>Validated</th>
                      <th>Redeem Limit</th>
                      <th>Event ID</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businessCoupons.map((coupon) => {
                      let couponValue = 'N/A';
                      if (coupon.amount !== undefined && coupon.amount !== null) {
                        if (coupon.promoType === 'PERCENTAGE') {
                          couponValue = `${coupon.amount}%`;
                        } else if (coupon.promoType === 'FREE') {
                          couponValue = '$0 (Free)';
                        } else {
                          couponValue = `$${coupon.amount}`;
                        }
                      }
                      
                      return (
                        <tr key={coupon.id}>
                          <td><strong>{coupon.code || coupon.id}</strong></td>
                          <td>
                            <strong className="text-success">{couponValue}</strong>
                          </td>
                          <td>{coupon.promoType || 'N/A'}</td>
                          <td>
                            {coupon.isUsed ? (
                              <Badge bg="danger">Used (Invalid)</Badge>
                            ) : coupon.isValid ? (
                              <Badge bg="success">Valid</Badge>
                            ) : (
                              <Badge bg="danger">Invalid</Badge>
                            )}
                          </td>
                          <td>{coupon.redeem || 'Unlimited'}</td>
                          <td><small className="text-muted">{coupon.eventId ? coupon.eventId.substring(0, 8) + '...' : 'N/A'}</small></td>
                          <td>{formatDate(coupon.createdAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                </>
              )}
            </div>
          )}

          {/* Views Tab */}
          {activeTab === "views" && (
            <div>
              {loadingViews ? (
                <div className="text-center">
                  <Spinner animation="border" />
                  <p className="mt-2">Loading event views...</p>
                </div>
              ) : (
                <>
                  {/* Date Range Selector */}
                  <Card className="mb-3">
                    <Card.Body>
                      <Row className="align-items-end">
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>
                              <FaCalendarAlt className="me-2" />
                              Start Date
                            </Form.Label>
                            <DatePicker
                              selected={viewsStartDate}
                              onChange={(date) => setViewsStartDate(date)}
                              selectsStart
                              startDate={viewsStartDate}
                              endDate={viewsEndDate}
                              maxDate={viewsEndDate}
                              dateFormat="yyyy-MM-dd"
                              className="form-control"
                              wrapperClassName="w-100"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>
                              <FaCalendarAlt className="me-2" />
                              End Date
                            </Form.Label>
                            <DatePicker
                              selected={viewsEndDate}
                              onChange={(date) => setViewsEndDate(date)}
                              selectsEnd
                              startDate={viewsStartDate}
                              endDate={viewsEndDate}
                              minDate={viewsStartDate}
                              dateFormat="yyyy-MM-dd"
                              className="form-control"
                              wrapperClassName="w-100"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Button
                            variant="primary"
                            onClick={handleApplyViewsDateRange}
                            className="w-100"
                          >
                            Apply Filter
                          </Button>
                        </Col>
                        <Col md={3}>
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              const defaultStart = moment().startOf('day').subtract(30, 'days').toDate();
                              const defaultEnd = moment().endOf('day').toDate();
                              setViewsStartDate(defaultStart);
                              setViewsEndDate(defaultEnd);
                              // Filter using the new dates directly
                              const start = moment(defaultStart).startOf('day').toDate();
                              const end = moment(defaultEnd).endOf('day').toDate();
                              const filtered = allBusinessViews.filter(view => {
                                const viewDate = view.timestamp instanceof Date ? view.timestamp : new Date(view.timestamp);
                                return viewDate >= start && viewDate <= end;
                              });
                              setBusinessViews(filtered);
                              setBusinessAnalytics(prev => ({ 
                                ...prev, 
                                totalViews: filtered.length 
                              }));
                            }}
                            className="w-100"
                          >
                            Reset to Last 30 Days
                          </Button>
                        </Col>
                      </Row>
                      <Row className="mt-2">
                        <Col>
                          <Alert variant="info" className="mb-0">
                            <small>
                              Showing <strong>{businessViews.length}</strong> of <strong>{allBusinessViews.length}</strong> views
                              {businessViews.length > 0 && (
                                <span>
                                  {' '}({moment(viewsStartDate).format('YYYY-MM-DD')} to {moment(viewsEndDate).format('YYYY-MM-DD')})
                                </span>
                              )}
                            </small>
                          </Alert>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <Alert variant="info" className="mb-3">
                    Showing page views for URLs containing <code>/lifestyles</code> or <code>/lifestyle/</code>
                  </Alert>

                  {businessViews.length === 0 ? (
                    <Alert variant="warning">
                      No lifestyle event views found for the selected date range.
                    </Alert>
                  ) : (
                    <>
                      {/* Analytics Section */}
                      <Row className="mb-4">
                        <Col md={6}>
                          <Card>
                            <Card.Header>
                              <h5 className="mb-0">Most Active Visitors</h5>
                            </Card.Header>
                            <Card.Body>
                              {getMostActiveVisitors().length === 0 ? (
                                <Alert variant="info" className="mb-0">
                                  No logged-in visitors found.
                                </Alert>
                              ) : (
                                <Table responsive striped bordered hover size="sm">
                                  <thead>
                                    <tr>
                                      <th>Rank</th>
                                      <th>User</th>
                                      <th>Visits</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {getMostActiveVisitors().map((visitor, index) => {
                                      const userInfo = visitor.userInfo;
                                      const userName = userInfo 
                                        ? (userInfo.displayName || `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() || userInfo.email || 'Unknown User')
                                        : 'Unknown User';
                                      const userEmail = userInfo?.email || 'N/A';
                                      
                                      return (
                                        <tr key={visitor.uid}>
                                          <td>
                                            <Badge bg={index === 0 ? "warning" : index === 1 ? "secondary" : index === 2 ? "info" : "light"} text={index > 2 ? "dark" : undefined}>
                                              #{index + 1}
                                            </Badge>
                                          </td>
                                          <td>
                                            <div>
                                              <strong>{userName}</strong>
                                              <br />
                                              <small className="text-muted">{userEmail}</small>
                                              <br />
                                              <Button
                                                size="sm"
                                                variant="link"
                                                className="p-0"
                                                onClick={() => window.open(`/user/${visitor.uid}`, '_blank')}
                                              >
                                                <small>View Details</small>
                                              </Button>
                                            </div>
                                          </td>
                                          <td>
                                            <Badge bg="primary" className="fs-6 px-3 py-2">
                                              {visitor.count}
                                            </Badge>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={6}>
                          <Card>
                            <Card.Header>
                              <h5 className="mb-0">Most Viewed Businesses</h5>
                            </Card.Header>
                            <Card.Body>
                              {getMostViewedBusinesses().length === 0 ? (
                                <Alert variant="info" className="mb-0">
                                  No business views found.
                                </Alert>
                              ) : (
                                <Table responsive striped bordered hover size="sm">
                                  <thead>
                                    <tr>
                                      <th>Rank</th>
                                      <th>Business</th>
                                      <th>Views</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {getMostViewedBusinesses().map((business, index) => (
                                      <tr key={business.eventId}>
                                        <td>
                                          <Badge bg={index === 0 ? "warning" : index === 1 ? "secondary" : index === 2 ? "info" : "light"} text={index > 2 ? "dark" : undefined}>
                                            #{index + 1}
                                          </Badge>
                                        </td>
                                        <td>
                                          <div>
                                            <strong>{business.businessName}</strong>
                                            <br />
                                            <small className="text-muted">{business.eventId.substring(0, 12)}...</small>
                                            <br />
                                            <Button
                                              size="sm"
                                              variant="link"
                                              className="p-0"
                                              onClick={() => window.open(`https://www.eventgo.ca/lifestyle/${business.eventId}`, '_blank')}
                                            >
                                              <small>View Business</small>
                                            </Button>
                                          </div>
                                        </td>
                                        <td>
                                          <Badge bg="success" className="fs-6 px-3 py-2">
                                            {business.count}
                                          </Badge>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>

                      {/* Daily View Counts */}
                      <Card className="mb-4">
                        <Card.Header>
                          <h5 className="mb-0">Views by Day</h5>
                        </Card.Header>
                        <Card.Body>
                          <Table responsive striped bordered hover size="sm">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Views</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getDailyViewCounts().map((dayData) => (
                                <tr key={dayData.date}>
                                  <td>
                                    <strong>{dayData.displayDate}</strong>
                                    <br />
                                    <small className="text-muted">{dayData.date}</small>
                                  </td>
                                  <td>
                                    <Badge bg="info" className="fs-6 px-3 py-2">
                                      {dayData.count}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <th>Total</th>
                                <th>
                                  <Badge bg="primary" className="fs-6 px-3 py-2">
                                    {businessViews.length}
                                  </Badge>
                                </th>
                              </tr>
                            </tfoot>
                          </Table>
                        </Card.Body>
                      </Card>

                      {/* Detailed Views Table */}
                      <Card>
                        <Card.Header>
                          <h5 className="mb-0">Detailed View List</h5>
                        </Card.Header>
                        <Card.Body>
                          <Table responsive striped bordered hover>
                    <thead>
                      <tr>
                        <th>URL</th>
                        <th>Event ID</th>
                        <th>User</th>
                        <th>Timestamp</th>
                        <th>IP Address</th>
                        <th>Platform</th>
                        <th>Device</th>
                        <th>Location</th>
                        <th>User Agent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {businessViews.map((view) => {
                        const userInfo = view.uid ? userDataMap[view.uid] : null;
                        const userName = userInfo 
                          ? (userInfo.displayName || `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() || userInfo.email || 'Unknown User')
                          : 'Anonymous';
                        const userEmail = userInfo?.email || 'N/A';
                        
                        return (
                          <tr key={view.id}>
                            <td>
                              <small className="text-break" style={{ maxWidth: '300px', display: 'block' }}>
                                {view.url || 'N/A'}
                              </small>
                            </td>
                            <td>
                              {view.eventId ? (
                                <small className="text-muted">{view.eventId.substring(0, 12)}...</small>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {view.uid ? (
                                <div>
                                  <div><strong>{userName}</strong></div>
                                  <small className="text-muted">{userEmail}</small>
                                  {userInfo?.phone && (
                                    <div><small className="text-muted">{userInfo.phone}</small></div>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="link"
                                    className="p-0 mt-1"
                                    onClick={() => window.open(`/user/${view.uid}`, '_blank')}
                                  >
                                    <small>View Details</small>
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-muted">Anonymous</span>
                              )}
                            </td>
                            <td>{formatDate(view.timestamp)}</td>
                            <td><small>{view.currentIP}</small></td>
                            <td>{view.platform}</td>
                            <td>{view.device}</td>
                            <td>{view.location}</td>
                            <td>
                              <small className="text-muted" style={{ maxWidth: '200px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {view.fromUA !== 'N/A' ? view.fromUA.substring(0, 50) + '...' : 'N/A'}
                              </small>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                        </Card.Body>
                      </Card>
                    </>
                  )}
                </>
              )}
            </div>
          )}

        </Card.Body>
      </Card>

      {/* Business Details Modal */}
      <Modal show={showBusinessModal} onHide={() => setShowBusinessModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Business Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
              {selectedBusiness && (
            <ListGroup variant="flush">
              <ListGroup.Item>
                <strong>ID:</strong> {selectedBusiness.id}
              </ListGroup.Item>
              <ListGroup.Item>
                <strong>Name:</strong> {selectedBusiness.name || 'N/A'}
              </ListGroup.Item>
              <ListGroup.Item>
                <strong>Email:</strong> {selectedBusiness.email || 'N/A'}
              </ListGroup.Item>
              <ListGroup.Item>
                <strong>Phone:</strong> {selectedBusiness.phone || 'N/A'}
              </ListGroup.Item>
              <ListGroup.Item>
                <strong>Location:</strong> {selectedBusiness.location || 'N/A'}
              </ListGroup.Item>
              <ListGroup.Item>
                <strong>Category:</strong> {selectedBusiness.category || 'N/A'}
              </ListGroup.Item>
              <ListGroup.Item>
                <strong>Sales:</strong> <span className="text-success fw-bold">${(selectedBusiness.sales || 0).toFixed(2)}</span>
              </ListGroup.Item>
              <ListGroup.Item>
                <strong>Views:</strong> <span className="text-info fw-bold">{selectedBusiness.views || 0}</span>
              </ListGroup.Item>
              <ListGroup.Item>
                <strong>Approved:</strong> {getApprovedBadge(selectedBusiness.approved)}
              </ListGroup.Item>
              <ListGroup.Item>
                <strong>Age Range:</strong>{' '}
                {selectedBusiness.ageRange && selectedBusiness.ageRange.trim()
                  ? selectedBusiness.ageRange
                  : 'N/A'}
              </ListGroup.Item>
              <ListGroup.Item>
                <strong>Created At:</strong> {formatDate(selectedBusiness.createdAt)}
              </ListGroup.Item>
              {selectedBusiness.description && (
                <ListGroup.Item>
                  <strong>Description:</strong>
                  <p>{selectedBusiness.description}</p>
                </ListGroup.Item>
              )}
              {selectedBusiness.website && (
                <ListGroup.Item>
                  <strong>Website:</strong>{' '}
                  <a href={selectedBusiness.website} target="_blank" rel="noopener noreferrer">
                    {selectedBusiness.website}
                  </a>
                </ListGroup.Item>
              )}
            </ListGroup>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBusinessModal(false)}>
            Close
          </Button>
          {selectedBusiness && (
            <>
              <Button variant="info" onClick={() => {
                setShowBusinessModal(false);
                navigate(`/admin-business-management/${selectedBusiness.id}`);
              }}>
                Modify Business
              </Button>
              <Button variant="primary" onClick={() => {
                setShowBusinessModal(false);
                handleEditBusiness(selectedBusiness);
              }}>
                Edit Business
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Edit Business Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Business</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingBusiness && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editingBusiness.name || ''}
                  onChange={(e) => setEditingBusiness({ ...editingBusiness, name: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={editingBusiness.email || ''}
                  onChange={(e) => setEditingBusiness({ ...editingBusiness, email: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="text"
                  value={editingBusiness.phone || ''}
                  onChange={(e) => setEditingBusiness({ ...editingBusiness, phone: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Location</Form.Label>
                <Form.Control
                  type="text"
                  value={editingBusiness.location || ''}
                  onChange={(e) => setEditingBusiness({ ...editingBusiness, location: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Control
                  type="text"
                  value={editingBusiness.category || ''}
                  onChange={(e) => setEditingBusiness({ ...editingBusiness, category: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Approved"
                  checked={editingBusiness.approved === true}
                  onChange={(e) => setEditingBusiness({ ...editingBusiness, approved: e.target.checked })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Age Range</Form.Label>
                <div>
                  <Form.Check
                    type="checkbox"
                    id="ageRange-0-3"
                    label="0 - 3"
                    checked={(editingBusiness.ageRange || '').includes('0-3')}
                    onChange={(e) => {
                      const currentRange = editingBusiness.ageRange || '';
                      const ranges = currentRange ? currentRange.split(',').filter(r => r.trim()) : [];
                      let newRanges;
                      if (e.target.checked) {
                        if (!ranges.includes('0-3')) {
                          newRanges = [...ranges, '0-3'];
                        } else {
                          newRanges = ranges;
                        }
                      } else {
                        newRanges = ranges.filter(r => r !== '0-3');
                      }
                      setEditingBusiness({ ...editingBusiness, ageRange: newRanges.join(',') });
                    }}
                  />
                  <Form.Check
                    type="checkbox"
                    id="ageRange-3-6"
                    label="3 - 6"
                    checked={(editingBusiness.ageRange || '').includes('3-6')}
                    onChange={(e) => {
                      const currentRange = editingBusiness.ageRange || '';
                      const ranges = currentRange ? currentRange.split(',').filter(r => r.trim()) : [];
                      let newRanges;
                      if (e.target.checked) {
                        if (!ranges.includes('3-6')) {
                          newRanges = [...ranges, '3-6'];
                        } else {
                          newRanges = ranges;
                        }
                      } else {
                        newRanges = ranges.filter(r => r !== '3-6');
                      }
                      setEditingBusiness({ ...editingBusiness, ageRange: newRanges.join(',') });
                    }}
                  />
                  <Form.Check
                    type="checkbox"
                    id="ageRange-6plus"
                    label="6+"
                    checked={(editingBusiness.ageRange || '').includes('6+')}
                    onChange={(e) => {
                      const currentRange = editingBusiness.ageRange || '';
                      const ranges = currentRange ? currentRange.split(',').filter(r => r.trim()) : [];
                      let newRanges;
                      if (e.target.checked) {
                        if (!ranges.includes('6+')) {
                          newRanges = [...ranges, '6+'];
                        } else {
                          newRanges = ranges;
                        }
                      } else {
                        newRanges = ranges.filter(r => r !== '6+');
                      }
                      setEditingBusiness({ ...editingBusiness, ageRange: newRanges.join(',') });
                    }}
                  />
                </div>
              </Form.Group>
              {editingBusiness.description !== undefined && (
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={editingBusiness.description || ''}
                    onChange={(e) => setEditingBusiness({ ...editingBusiness, description: e.target.value })}
                  />
                </Form.Group>
              )}
              {editingBusiness.website !== undefined && (
                <Form.Group className="mb-3">
                  <Form.Label>Website</Form.Label>
                  <Form.Control
                    type="url"
                    value={editingBusiness.website || ''}
                    onChange={(e) => setEditingBusiness({ ...editingBusiness, website: e.target.value })}
                  />
                </Form.Group>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveBusiness} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
